import React from "react"
import moment from "moment"
import { graphql } from "@apollo/client/react/hoc"
import gql from "graphql-tag"
import "./InspectionDocumentsSection.css"
import { cleanDocumentUrl } from "../../Material/DocumentModal/DocumentsListNew"
import { isImage, isPdf, isVideo } from "../../Material/DocumentModal/utils"

const AllCarPicturesQuery = gql`
    query AllCarPictures($incidentId: ID!) {
        allCarPictures(incidentId: $incidentId) {
        edges {
            node {
                id
                dateAdded
                pictureUrl
                pictureType
                description
                formType
            }
        }
        }
    }
`

const getInspectionGroups = (pictures) => {
    const groupsByTime = pictures.reduce((acc, pic) => {
        const dateAdded = pic.node?.dateAdded
        const date = dateAdded ? moment(dateAdded) : null
        const key = date?.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "unknown"

        if (!acc[key]) {
            acc[key] = {
                key,
                date,
                pictures: [],
            }
        }
        acc[key].pictures.push(pic)
        return acc
    }, {})

    return Object.values(groupsByTime).sort((a, b) => {
        if (!a.date?.isValid()) return 1
        if (!b.date?.isValid()) return -1
        return b.date.valueOf() - a.date.valueOf()
    })
}

const getPictureLabel = (pictureType) => {
    const label = (pictureType || "Other").replace(/_/g, " ")
    if (label.toLowerCase().includes("walkaround")) return "Walkaround"
    return label
}

const renderPreview = (pictureUrl, pictureType, idx) => {
    if (isImage(pictureUrl)) {
        return <img className="inspection-timeline-thumb-media" src={pictureUrl} alt={pictureType || "Inspection"} />
    }
    if (isVideo(pictureUrl, pictureType)) {
        return <video className="inspection-timeline-thumb-media" src={pictureUrl} playsInline muted preload="metadata" />
    }
    if (isPdf(pictureUrl, pictureType)) {
        return <iframe scrolling="no" className="inspection-timeline-thumb-media" src={pictureUrl} title={`inspection-iframe-${idx}`} />
    }
    return (
        <div className="inspection-timeline-thumb-fallback">
            <i className="fa fa-file-text-o" aria-hidden="true" />
        </div>
    )
}

const InspectionDocumentsSection = ({ pictures = [], picturesLoading = false, loading = false, enableSelection = true, selectedDocs = [], onToggleSelect, onPreview, }) => {
    if (!pictures.length) {
        if (picturesLoading) return null

        return (
            <div className="documents-empty-state-wrap">
                <div className="documents-empty-state">
                    <span className="documents-empty-state__icon">
                        <i className="fa fa-camera" aria-hidden="true" />
                    </span>
                    <h3 className="documents-empty-state__title mb-0">No Documents Yet</h3>
                    <p className="documents-empty-state__description">
                        Upload documents or request missing ones to keep this section complete.
                    </p>
                </div>
            </div>
        )
    }

    const groups = getInspectionGroups(pictures)
    const yearLabel = groups[0]?.date?.isValid() ? groups[0].date.format("YYYY") : "Unknown"

    return (
        <div className="inspection-timeline">
            <div className="inspection-timeline-year">
                <span className="inspection-timeline-year-dot" />
                <span>{yearLabel}</span>
            </div>
            {groups.map((group) => (
                <div className="inspection-timeline-row" key={group.key}>
                    <div className="inspection-timeline-date">
                        <span>{group.date?.isValid() ? group.date.format("YYYY-MM-DD") : "--"}</span>
                        <span>{group.date?.isValid() ? group.date.format("h:mm a") : "--"}</span>
                    </div>
                    <div className="inspection-timeline-node" />
                    <div className="inspection-timeline-content">
                        <div className="inspection-timeline-title">Inspection</div>
                        <div className="inspection-timeline-source">{group.pictures[0]?.node?.formType || "VGM Admin App"}</div>
                        <div className="inspection-timeline-gallery">
                            {group.pictures.map((pic) => {
                                const picture = pic.node
                                const globalIndex = pictures.findIndex((item) => item.node?.id === picture.id)
                                const pictureUrl = cleanDocumentUrl(picture.pictureUrl)
                                const pictureType = picture.pictureType || "Inspection Photo"
                                const isSelected = !!selectedDocs.find((doc) => doc.id === picture.id)

                                const previewIndex = globalIndex >= 0 ? globalIndex : 0

                                return (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className={`inspection-timeline-thumb${loading ? " is-disabled" : ""}`}
                                        key={picture.id}
                                        onClick={() => !loading && onPreview(pictures, previewIndex)}
                                        onKeyDown={(e) => {
                                            if (!loading && (e.key === "Enter" || e.key === " ")) {
                                                e.preventDefault()
                                                onPreview(pictures, previewIndex)
                                            }
                                        }}
                                    >
                                        {enableSelection && isImage(pictureUrl) && (
                                            <input
                                                type="checkbox"
                                                className="inspection-timeline-checkbox"
                                                checked={isSelected}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => onToggleSelect({
                                                    id: picture.id,
                                                    pictureUrl: picture.pictureUrl,
                                                    documentType: { typeName: pictureType },
                                                })}
                                            />
                                        )}
                                        {renderPreview(pictureUrl, picture.pictureType, globalIndex)}
                                        <span className="inspection-timeline-thumb-label">{getPictureLabel(picture.pictureType)}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

export default graphql(AllCarPicturesQuery, {
    skip: ({ incidentId }) => !incidentId,
    options: ({ incidentId }) => ({
        variables: { incidentId },
        fetchPolicy: "network-only"
    }),
    props: ({ data: { allCarPictures, loading } }) => ({
        pictures: allCarPictures?.edges || [],
        picturesLoading: loading,
    }),
})(InspectionDocumentsSection)
